/*
  SwingHistory — saved AI-coach analyses.
  ------------------------------------------------------------------------
  Lists the user's most recent Swing Coach analyses. Each card shows:
    - Date + tempo score chip
    - Key frame note
    - Top strength + top improvement (compact preview)
    - "View" → expands into the full analysis with the drill embed

  Fetches GET /api/swings/mine on mount. Individual "View" opens the
  full SwingCoach-style card inline (no separate detail route needed —
  keeps state simple and everything on one screen for quick scroll).
*/
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Barbell, Trophy, Warning, Trash, Play, VideoCamera } from "@phosphor-icons/react";
import { api } from "../lib/api";
import SwingStudio from "../components/SwingStudio";

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })
      + " · "
      + d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

export default function SwingHistory() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  // Cache of swing_id → blob object URL for the playback <video>. Fetched
  // lazily on expand so the list stays cheap. Revoked on unmount to avoid
  // leaking blob memory across long sessions.
  const [videoUrls, setVideoUrls] = useState({});
  const [videoLoading, setVideoLoading] = useState({});
  const videoUrlsRef = useRef({});
  // When set, mount the fullscreen SwingStudio overlay for this swing.
  // We render it out of the expanded card so the studio can go full-viewport
  // rather than being cramped inside the list row.
  const [studioSwing, setStudioSwing] = useState(null);

  useEffect(() => () => {
    Object.values(videoUrlsRef.current).forEach((u) => {
      try { URL.revokeObjectURL(u); } catch { /* noop */ }
    });
  }, []);

  const loadVideo = async (id) => {
    if (videoUrlsRef.current[id] || videoLoading[id]) return;
    setVideoLoading((v) => ({ ...v, [id]: true }));
    try {
      const res = await api.get(`/swings/${id}/video`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      videoUrlsRef.current[id] = url;
      setVideoUrls((v) => ({ ...v, [id]: url }));
    } catch {
      // Silently ignore — the UI just won't show the player. The rest of
      // the analysis card (strengths, drill, etc.) still renders fine.
    } finally {
      setVideoLoading((v) => ({ ...v, [id]: false }));
    }
  };

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/swings/mine");
      setItems(data.analyses || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't load your saved swings");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this swing analysis? Can't undo.")) return;
    try {
      await api.delete(`/swings/${id}`);
      setItems((cur) => cur.filter((a) => a.id !== id));
      if (expandedId === id) setExpandedId(null);
      const url = videoUrlsRef.current[id];
      if (url) {
        try { URL.revokeObjectURL(url); } catch { /* noop */ }
        delete videoUrlsRef.current[id];
        setVideoUrls((v) => { const n = { ...v }; delete n[id]; return n; });
      }
    } catch (e) {
      alert(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-4 pb-24" data-testid="swing-history-page">
      <div className="flex items-center gap-2 mb-4">
        <Link to="/app/swing-lab" className="p-2 -ml-2 rounded-full hover:bg-black/5" data-testid="swing-history-back" aria-label="Back">
          <ArrowLeft size={18} weight="bold" />
        </Link>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-[var(--text-secondary)]">Swing Coach</p>
          <h1 className="font-heading font-black text-2xl text-[var(--primary)] leading-tight">Saved swings</h1>
        </div>
      </div>

      {loading && <p className="text-sm text-[var(--text-secondary)]" data-testid="swing-history-loading">Loading your saved swings…</p>}

      {!loading && error && (
        <p className="text-sm text-[var(--status-bad)]" data-testid="swing-history-error">{error}</p>
      )}

      {!loading && !error && items.length === 0 && (
        <div className="surface-card p-5 text-center" data-testid="swing-history-empty">
          <VideoCamera size={28} weight="fill" className="mx-auto mb-2" style={{ color: "var(--accent-terracotta)" }} />
          <p className="font-heading font-bold text-[var(--primary)] mb-1">No saved swings yet</p>
          <p className="text-xs text-[var(--text-secondary)] mb-3">Analyse a swing in Swing Lab or during a round and it&apos;ll turn up here so you can review your progress.</p>
          <Link to="/app/swing-lab" className="btn-primary inline-block" data-testid="swing-history-empty-cta">
            Open Swing Lab
          </Link>
        </div>
      )}

      <div className="space-y-2">
        {items.map((a) => {
          const isOpen = expandedId === a.id;
          return (
            <div key={a.id} className="surface-card overflow-hidden" data-testid={`swing-row-${a.id}`}>
              <button
                type="button"
                onClick={() => {
                  const next = isOpen ? null : a.id;
                  setExpandedId(next);
                  if (next && a.has_video) loadVideo(a.id);
                }}
                className="w-full text-left p-3 flex items-center gap-3 hover:bg-black/2"
                data-testid={`swing-row-toggle-${a.id}`}
              >
                <div className="w-11 h-11 rounded-full flex flex-col items-center justify-center text-white leading-none" style={{ background: "var(--primary)" }}>
                  <span className="font-mono-num font-black text-base">{a.tempo_score}</span>
                  <span className="text-[8px] opacity-70">/10</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-widest text-[var(--text-secondary)] font-bold leading-none">{formatDate(a.created_at)}</p>
                  <p className="font-heading font-bold text-sm text-[var(--primary)] truncate mt-0.5">
                    {a.improvements?.[0] || a.key_frame_notes || "Swing analysis"}
                  </p>
                </div>
                <Play size={14} weight="fill" className={`opacity-50 transition-transform ${isOpen ? "rotate-90" : ""}`} />
              </button>

              {isOpen && (
                <div className="p-3 border-t border-[var(--border-default)] space-y-2" data-testid={`swing-row-detail-${a.id}`}>
                  {a.has_video && (
                    <div className="rounded-lg overflow-hidden bg-black aspect-video relative" data-testid={`swing-row-video-${a.id}`}>
                      {videoUrls[a.id] ? (
                        <>
                          <video
                            src={videoUrls[a.id]}
                            muted
                            playsInline
                            className="w-full h-full object-contain"
                            data-testid={`swing-row-video-player-${a.id}`}
                          />
                          <button
                            type="button"
                            onClick={() => setStudioSwing({ id: a.id, url: videoUrls[a.id], analysis: a })}
                            className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/50 transition-colors"
                            data-testid={`swing-row-open-studio-${a.id}`}
                            aria-label="Open Swing Studio"
                          >
                            <div className="flex items-center gap-2 bg-white text-black rounded-full px-4 py-2 font-heading font-bold text-sm shadow-lg">
                              <Play size={16} weight="fill" />
                              Open Studio
                            </div>
                          </button>
                        </>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-white text-xs opacity-70">
                          {videoLoading[a.id] ? "Loading your swing…" : "Tap to load swing"}
                        </div>
                      )}
                    </div>
                  )}
                  {a.strengths?.length > 0 && (
                    <div className="rounded-lg p-2" style={{ background: "rgba(16,185,129,.10)" }}>
                      <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: "#0F9C6C" }}>
                        <Trophy size={12} weight="fill" /> Doing well
                      </div>
                      <ul className="text-xs space-y-0.5">
                        {a.strengths.slice(0, 3).map((s, i) => <li key={i}>▸ {s}</li>)}
                      </ul>
                    </div>
                  )}
                  {a.improvements?.length > 0 && (
                    <div className="rounded-lg p-2" style={{ background: "rgba(196,84,60,.10)" }}>
                      <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: "var(--accent-terracotta)" }}>
                        <Warning size={12} weight="fill" /> Work on
                      </div>
                      <ul className="text-xs space-y-0.5">
                        {a.improvements.slice(0, 3).map((s, i) => <li key={i}>▸ {s}</li>)}
                      </ul>
                    </div>
                  )}
                  {a.drill && (
                    <div className="rounded-lg p-3" style={{ background: "var(--primary)", color: "#fff" }}>
                      <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold opacity-70">
                        <Barbell size={11} weight="fill" /> Drill
                      </div>
                      <p className="text-sm font-bold mt-1 leading-snug">{a.drill}</p>
                      {a.drill_search_query && (
                        <div className="mt-2 rounded-lg overflow-hidden aspect-video bg-black">
                          <iframe
                            title="Drill demo"
                            src={`https://www.youtube-nocookie.com/embed?listType=search&list=${encodeURIComponent(a.drill_search_query)}&modestbranding=1&rel=0`}
                            width="100%"
                            height="100%"
                            style={{ border: 0 }}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        </div>
                      )}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => del(a.id)}
                    className="w-full flex items-center justify-center gap-1 text-[11px] font-bold text-[var(--status-bad)] py-1"
                    data-testid={`swing-row-delete-${a.id}`}
                  >
                    <Trash size={12} weight="bold" /> Delete this swing
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {studioSwing && (
        <SwingStudio
          videoSrc={studioSwing.url}
          analysis={studioSwing.analysis}
          onClose={() => setStudioSwing(null)}
        />
      )}
    </div>
  );
}
